import type { WaterQualityData } from "@/lib/conditions/water-quality";
import type { SatelliteWaterQuality } from "@/lib/conditions/satellite-water-quality";
import { storedOceanCurrentMs } from "@/lib/conditions/daily-obs-types";
import type { WeatherHourly, MarineHourly } from "@/lib/conditions/daily-obs-types";
import { WaterQualityBadge } from "./WaterQualityBadge";
import { WaterQualityPanel } from "./WaterQualityPanel";
import { degToCompass } from "@/lib/conditions/scoring";

type WaterQualitySectionProps = {
  compact?: boolean;
  waterQuality: WaterQualityData | null;
  satelliteWQ: SatelliteWaterQuality | null;
  currentHourWeather: WeatherHourly | null;
  currentHourMarine: MarineHourly | null;
};

export function WaterQualitySection({
  compact = false,
  waterQuality,
  satelliteWQ,
  currentHourWeather,
  currentHourMarine,
}: WaterQualitySectionProps) {
  const hasSatellite = satelliteWQ !== null;
  const hasDischarge = waterQuality !== null;
  const hasSst = currentHourMarine?.sst_c != null && currentHourWeather?.temp_c != null;
  const currentMs = storedOceanCurrentMs(currentHourMarine);
  const hasCurrent = currentMs !== null;

  if (!hasSatellite && !hasDischarge && !hasSst && !hasCurrent) return null;

  const sstDiff = hasSst
    ? (currentHourMarine!.sst_c! - currentHourWeather!.temp_c).toFixed(1)
    : null;
  const sstWarmer = hasSst ? currentHourMarine!.sst_c! > currentHourWeather!.temp_c : false;

  return (
    <section className={compact ? "" : "max-w-[1100px] mx-auto px-5 md:px-12 py-8 md:py-10"}>
      <h2 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-5">
        Water quality and environment
      </h2>

      <div className="space-y-4">
        {hasSatellite && <WaterQualityBadge data={satelliteWQ!} />}
        {hasDischarge && <WaterQualityPanel data={waterQuality!} />}

        {(hasSst || hasCurrent) && (
          <div className="flex flex-wrap gap-3">
            {hasSst && (
              <div className="bg-warm-bg rounded-[10px] px-3.5 py-3 min-w-[140px]">
                <div className="text-[10px] font-semibold uppercase tracking-[0.8px] text-text-muted">
                  Sea surface temp
                </div>
                <div className="text-[14px] font-semibold text-navy tabular-nums mt-0.5">
                  {currentHourMarine!.sst_c!.toFixed(1)}°C
                </div>
                <div className="text-[11px] text-text-muted mt-0.5">
                  {Math.abs(Number(sstDiff))}° {sstWarmer ? "warmer" : "cooler"} than air
                </div>
              </div>
            )}

            {hasCurrent && (
              <div className="bg-warm-bg rounded-[10px] px-3.5 py-3 min-w-[140px]">
                <div className="text-[10px] font-semibold uppercase tracking-[0.8px] text-text-muted">
                  Ocean current
                </div>
                <div className="text-[14px] font-semibold text-navy tabular-nums mt-0.5">
                  {currentMs!.toFixed(2)} m/s
                </div>
                {currentHourMarine!.ocean_current_dir_deg != null && (
                  <div className="text-[11px] text-text-muted mt-0.5">
                    Flowing {degToCompass(currentHourMarine!.ocean_current_dir_deg!)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
